import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./tokenlyglobal.css";

const Wallet = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [walletData, setWalletData] = useState(null);
  const [rechargeAmount, setRechargeAmount] = useState("5");
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Predefined recharge amounts
  const quickAmounts = [50, 100, 200];

  useEffect(() => {
    // Check if user is logged in
    const stored = localStorage.getItem("tokenly_user");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setUser(parsed);
      } catch {
        setUser(null);
      }
    } else {
      setUser(null);
    }

    fetchWalletData();
  }, []);

  useEffect(() => {
    // Scroll to top and center wallet card after component is fully loaded
    window.scrollTo(0, 0);

    // Wait for component to fully render before centering
    const timer = setTimeout(() => {
      const card = document.getElementById('wallet-card');
      if (card) {
        // First scroll to top, then center the card
        window.scrollTo(0, 0);
        setTimeout(() => {
          card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 50);
      }
    }, 300); // Increased delay to ensure rendering is complete

    return () => clearTimeout(timer);
  }, [walletData]); // Run when wallet data is loaded

  const getAuthToken = () => {
    return localStorage.getItem("tokenly_token") || "";
  };

  const fetchWalletData = async () => {
    try {
      const authToken = getAuthToken();
      if (!authToken) {
        navigate("/login");
        return;
      }

      setLoading(true);

      const response = await fetch(
        "https://tokenly.biswaas.com/api/user/userDashboardData",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.status && result.data) {
        setWalletData(result.data);
      } else {
        console.error("Failed to fetch wallet data");
      }
    } catch (err) {
      console.error("Wallet fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const startPaytmPayment = async (amount) => {
    if (!user || !user.userId) {
      navigate("/login");
      return;
    }

    if (!amount || amount <= 0) {
      alert("Please enter a valid amount");
      return;
    }

    setIsProcessing(true);

    try {
      const orderId = "TY_" + Date.now();
      const res = await fetch("https://tokenly.biswaas.com/api/paytm/initiate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderId: orderId,
          amount: amount.toFixed(2),
          userId: user.userId,
          purpose: "wallet_recharge", // Add purpose for wallet recharge
        }),
      });

      const data = await res.json();

      if (!data.data.txnToken) {
        alert("Failed to initiate payment");
        return;
      }

      openPaytmCheckout(orderId, data.data.txnToken, amount);
    } catch (error) {
      console.error("Payment initiation error:", error);
      alert("Failed to initiate payment. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const openPaytmCheckout = (orderId, txnToken, amount) => {
    const config = {
      root: "",
      flow: "DEFAULT",
      data: {
        orderId: orderId,
        token: txnToken,
        tokenType: "TXN_TOKEN",
        amount: amount.toFixed(2),
      },
      handler: {
        notifyMerchant: function (eventName, data) {
          console.log("Paytm Event:", eventName, data);

          // Handle payment completion
          if (eventName === "response") {
            // Refresh wallet data after successful payment
            setTimeout(() => {
              fetchWalletData();
              setRechargeAmount("");
            }, 2000);
          }
        },
      },
    };

    if (window.Paytm && window.Paytm.CheckoutJS) {
      window.Paytm.CheckoutJS.init(config)
        .then(() => {
          window.Paytm.CheckoutJS.invoke();
        })
        .catch((error) => {
          console.error("Paytm init error", error);
          alert("Payment initialization failed");
          setIsProcessing(false);
        });
    } else {
      setErrorMessage("Payment service not available");
      setIsProcessing(false);
    }
  };

  const handleRecharge = () => {
    const amount = parseFloat(rechargeAmount);
    setErrorMessage(""); // Clear previous error

    if (amount < 5) {
      setErrorMessage("Minimum recharge amount is ₹5");
      return;
    }
    if (amount > 800) {
      setErrorMessage("Maximum recharge amount is ₹800");
      return;
    }
    startPaytmPayment(amount);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading wallet...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Hero/Header Section - Using same background as contact */}
      <section className="contact-hero d-flex align-items-center">
        <div className="contact-hero-overlay"></div>
        <div className="container-fluid container-xl px-4 px-md-3">
          <h1 className="display-5 fw-bold text-white mb-1">My <span className="text-warning">Wallet</span></h1>
          <p className="text-light mb-0">
            Manage your wallet balance, recharge instantly, and track your token bookings all in one place.
          </p>
        </div>
      </section>

      {/* Main Wallet Content */}
      <div className="container pt-3 pt-md-4" style={{ minHeight: 'calc(100vh - 260px)' }}>
        <div className="row justify-content-center">
          <div className="col-12 col-md-11 col-lg-10 col-xl-9 px-2 px-md-3">

            {/* Premium Wallet Card - Horizontal Layout */}
            <div id="wallet-card" className="card shadow-lg border-0 overflow-hidden" style={{
              borderRadius: "24px",
              backgroundColor: "#ffffff",
              boxShadow: '0 20px 50px rgba(0,0,0,0.12) !important'
            }}>
              <div className="row g-0">
                {/* Left Side: Balance Information (Full on mobile, stacked) */}
                <div className="col-lg-5 d-flex flex-column justify-content-center p-4 p-md-5 text-white text-center" style={{
                  background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                }}>
                  <div className="text-white-50 small mb-2 fw-medium uppercase letter-spacing-1">Current Balance</div>
                  <div className="display-3 fw-bold mb-3" style={{ letterSpacing: '-1.5px', fontSize: 'clamp(3rem, 10vw, 4rem)' }}>
                    ₹{walletData?.balance || '0'}
                  </div>

                  <div className="mt-2 d-inline-flex align-items-center justify-content-center p-2 px-4 mx-auto" style={{
                    background: 'rgba(255,255,255,0.15)',
                    borderRadius: '100px',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255,255,255,0.2)'
                  }}>
                    <i className="fa-solid fa-ticket me-2 text-warning"></i>
                    <span className="fw-bold" style={{ fontSize: '0.9rem' }}>{walletData?.free_token || 0} Free Tokens Available</span>
                  </div>

                  <div className="mt-4 pt-4 border-top border-white border-opacity-10 opacity-75 small d-none d-md-block">
                    <i className="fa-solid fa-check-double me-2"></i>
                    Your balance is updated instantly
                  </div>
                </div>

                {/* Right Side: Recharge Section */}
                <div className="col-lg-7 p-4 p-md-5 bg-white">
                  <div className="d-flex align-items-center mb-4">
                    <div className="bg-success bg-opacity-10 p-2 rounded-3 me-3 text-success">
                      <i className="fa-solid fa-plus-circle"></i>
                    </div>
                    <h5 className="fw-bold mb-0">Recharge Your Wallet</h5>
                  </div>

                  {/* Recharge Form Container */}
                  <div className="row g-4 pt-2">
                    <div className="col-md-6">
                      <label className="form-label small fw-bold text-muted mb-2">Select Quick Amount</label>
                      <div className="row g-2">
                        {quickAmounts.map((amount) => (
                          <div className="col-4" key={amount}>
                            <button
                              className={`btn w-100 py-2 fw-bold transition-all ${rechargeAmount === amount.toString()
                                ? 'btn-success shadow-sm'
                                : 'btn-outline-light text-dark border-1'
                                }`}
                              onClick={() => {
                                setRechargeAmount(amount.toString());
                                setErrorMessage("");
                              }}
                              style={{
                                borderRadius: '12px',
                                fontSize: '0.85rem',
                                backgroundColor: rechargeAmount === amount.toString() ? '#10b981' : 'transparent',
                                borderColor: rechargeAmount === amount.toString() ? 'transparent' : '#f1f5f9'
                              }}
                            >
                              ₹{amount}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label small fw-bold text-muted mb-2">Custom Amount (₹)</label>
                      <div className="input-group input-group-lg" style={{
                        borderRadius: '14px',
                        overflow: 'hidden',
                        background: '#f8fafc',
                        border: '1px solid #f1f5f9'
                      }}>
                        <input
                          type="number"
                          className="form-control border-0 bg-transparent py-2 fw-bold shadow-none"
                          placeholder="0"
                          value={rechargeAmount}
                          min="5"
                          max="800"
                          onKeyDown={(e) => {
                            if (["-", "e", "E", "+"].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onChange={(e) => {
                            const val = e.target.value;
                            setRechargeAmount(val);
                            if (parseFloat(val) > 800) {
                              setErrorMessage("Maximum recharge limit is ₹800");
                            } else if (val && parseFloat(val) < 5) {
                              setErrorMessage("Minimum recharge is ₹5");
                            } else {
                              setErrorMessage("");
                            }
                          }}
                        />
                      </div>
                      <div className="mt-1 ps-1">
                        {errorMessage ? (
                          <small className="text-danger fw-600 d-flex align-items-center">
                            <i className="fa-solid fa-circle-exclamation me-1 small"></i>
                            {errorMessage}
                          </small>
                        ) : (
                          <small className="text-muted opacity-75">Limit: ₹5 - ₹800</small>
                        )}
                      </div>
                    </div>

                    <div className="col-12 mt-4 pt-2">
                      <button
                        className="btn btn-success w-100 py-3 fw-bold shadow-sm border-0 d-flex align-items-center justify-content-center"
                        onClick={handleRecharge}
                        disabled={isProcessing || !rechargeAmount || parseFloat(rechargeAmount) < 5 || parseFloat(rechargeAmount) > 800}
                        style={{
                          background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                          borderRadius: '16px',
                          fontSize: '1.1rem',
                          height: '60px'
                        }}
                      >
                        {isProcessing ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Processing...
                          </>
                        ) : (
                          <>
                            Proceed to add ₹{rechargeAmount || '0'}
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-top border-light">
                    <div className="d-flex flex-column gap-3">
                      <div className="d-flex align-items-center justify-content-center text-muted small opacity-75">
                        <i className="fa-solid fa-shield-halved text-success me-2"></i>
                        Recharge amount is not refundable
                      </div>

                      <div className="d-flex flex-column flex-sm-row gap-2">
                        <button
                          className="btn btn-light text-dark flex-grow-1 py-2 fw-bold border"
                          onClick={() => navigate('/')}
                          style={{ borderRadius: '12px', fontSize: '0.85rem' }}
                        >
                          <i className="fa-solid fa-house-chimney me-2 opacity-50"></i>
                          Dashboard
                        </button>
                        <button
                          className="btn btn-primary flex-grow-1 py-2 fw-bold"
                          onClick={() => navigate('/alldoctors')}
                          style={{
                            borderRadius: '12px',
                            fontSize: '0.85rem',
                            background: '#3b82f6',
                            border: 'none',
                            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)'
                          }}
                        >
                          <i className="fa-solid fa-ticket me-2"></i>
                          Book Token
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>


          </div>
        </div>
      </div>

      <style jsx>{`
        .transition-all { transition: all 0.2s ease; }
        .letter-spacing-1 { letter-spacing: 1px; }
        .fw-600 { font-weight: 600; }
        .btn-outline-light:hover { 
          background: #f8fafc; 
          border-color: #cbd5e1;
          color: #1e293b;
        }
      `}</style>
    </>
  );
};

export default Wallet;
